import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link, withRouter } from "react-router-dom";
import { useQuery, useMutation } from "react-apollo-hooks";
import ReactMoment from "react-moment";
import { toast } from "react-toastify";
import {
  DONATION_DETAIL_QUERY,
  DONATION_TAX_FORM_TOKEN,
  DONATION_TAX_INFO_LIST_QUERY,
  DONATION_TAX_INFO_CREATE_QUERY,
  DONATION_TAX_INFO_DELETE_QUERY,
} from "../Queries";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../../../Components/Panel/Panel";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { graphqlUri } from "../../../../Config/Env";

export default withRouter(({ match, location }) => {
  const donationId = match.params.id;
  const now = new Date();

  const { data: dData } = useQuery(DONATION_DETAIL_QUERY, {
    variables: { id: donationId },
  });
  const donationTitle =
    dData && dData.AdminDonationDetail ? dData.AdminDonationDetail.title : "";

  // 소득공제신고 내역 데이터 연도 선택
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= 2020; y--) yearOptions.push(y);
  const [hometaxOpen, setHometaxOpen] = useState(false);
  const [hometaxYear, setHometaxYear] = useState(now.getFullYear());

  const onDownloadHometax = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${graphqlUri}/excels/donationHometax?donationId=${donationId}&year=${hometaxYear}`,
        {
          method: "POST",
          mode: "cors",
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }
      );
      if (!res.ok) {
        const msg = await res.text();
        toast.error(msg || "엑셀 다운로드에 실패했습니다.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `소득공제신고 내역_${hometaxYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      a.remove();
      setHometaxOpen(false);
    } catch (e) {
      console.log(e);
      toast.error("에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  const { data, loading, refetch } = useQuery(DONATION_TAX_INFO_LIST_QUERY, {
    variables: { donationId },
    fetchPolicy: "network-only",
  });
  const list = (data && data.AdminDonationTaxInfoList) || [];

  const [taxTokenMutation] = useMutation(DONATION_TAX_FORM_TOKEN);
  const [taxLink, setTaxLink] = useState("");
  const onShowTaxLink = async () => {
    try {
      const {
        data: { AdminDonationTaxFormToken: token },
      } = await taxTokenMutation({ variables: { donationId } });
      if (token) {
        setTaxLink(`${window.location.origin}/donation-tax-info/${token}`);
      } else {
        toast.error("링크 생성에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.toString().replace("Error: GraphQL error:", "").trim();
      toast.error(msg || "에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };
  const onCopyTaxLink = () => {
    if (!taxLink) return;
    navigator.clipboard
      .writeText(taxLink)
      .then(() => toast.success("링크가 복사되었습니다."))
      .catch(() => toast.error("복사에 실패했습니다. 직접 복사해주세요."));
  };

  const [createMutation] = useMutation(DONATION_TAX_INFO_CREATE_QUERY);
  const [deleteMutation] = useMutation(DONATION_TAX_INFO_DELETE_QUERY);
  const [name, setName] = useState("");
  const [residentNumber, setResidentNumber] = useState("");
  const [phone, setPhone] = useState("");

  const onAdd = async () => {
    if (!name.trim() || !residentNumber || !phone) {
      return toast.error("이름·주민번호·연락처를 모두 입력해주세요.");
    }
    try {
      const {
        data: { AdminDonationTaxInfoCreate: ok },
      } = await createMutation({
        variables: {
          donationId,
          name: name.trim(),
          residentNumber: residentNumber.replace(/\D/g, ""),
          phone: phone.replace(/\D/g, ""),
        },
      });
      if (ok) {
        toast.success("등록되었습니다.");
        setName("");
        setResidentNumber("");
        setPhone("");
        refetch();
      } else {
        toast.error("등록에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.toString().replace("Error: GraphQL error:", "").trim();
      toast.error(msg || "에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("이 항목을 삭제하시겠습니까?")) return;
    try {
      const {
        data: { AdminDonationTaxInfoDelete: ok },
      } = await deleteMutation({ variables: { id } });
      if (ok) {
        toast.success("삭제되었습니다.");
        refetch();
      } else {
        toast.error("삭제에 실패했습니다.");
      }
    } catch (e) {
      const msg = e.toString().replace("Error: GraphQL error:", "").trim();
      toast.error(msg || "에러가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <>
      <Helmet>
        <title>소득공제신고 내역 | 한밭페이</title>
      </Helmet>
      <div>
        <ol className="breadcrumb float-xl-right">
          <li className="breadcrumb-item">
            <Link to={`/donations${location.search}`}>기부</Link>
          </li>
          <li className="breadcrumb-item active">소득공제신고 내역</li>
        </ol>
        <h1 className="page-header">{donationTitle} 소득공제신고 내역</h1>

        <Panel>
          <PanelHeader noButton={true}>주민번호 수집 링크</PanelHeader>
          <PanelBody>
            <div className="alert alert-info f-s-13 m-b-10">
              <i className="fa fa-info-circle m-r-5"></i>
              아래 링크를 기부자에게 전달하면, 기부자가 직접
              <b> 이름·주민번호·연락처</b>를 제출합니다. <b>이 기부에 기부한
              연락처</b>일 때만 제출되며, 제출된 주민번호는 소득공제신고 내역
              데이터에 자동 반영됩니다.
            </div>
            {!taxLink ? (
              <button className="btn btn-primary" onClick={onShowTaxLink}>
                <i className="fa fa-link m-r-5"></i>수집 링크 생성/보기
              </button>
            ) : (
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={taxLink}
                  readOnly
                  onFocus={(e) => e.target.select()}
                />
                <span className="input-group-append">
                  <button className="btn btn-white" onClick={onCopyTaxLink}>
                    <i className="fa fa-copy m-r-5"></i>복사
                  </button>
                </span>
              </div>
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHeader noButton={true}>기부자 정보 직접 등록</PanelHeader>
          <PanelBody>
            <div className="alert alert-info f-s-13 m-b-10">
              <i className="fa fa-info-circle m-r-5"></i>
              폼 제출이 어려운 기부자를 대신 등록합니다. <b>이 기부에 기부한
              연락처</b>일 때만 등록됩니다(같은 연락처는 덮어쓰기).
            </div>
            <div className="row">
              <div className="col-md-3 m-b-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder="이름"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-md-3 m-b-5">
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="주민등록번호('-' 없이 13자리)"
                  maxLength={14}
                  value={residentNumber}
                  onChange={(e) => setResidentNumber(e.target.value)}
                />
              </div>
              <div className="col-md-3 m-b-5">
                <input
                  type="tel"
                  inputMode="numeric"
                  className="form-control"
                  placeholder="연락처(기부 시 사용한 휴대폰)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="col-md-2 m-b-5">
                <button className="btn btn-primary btn-block" onClick={onAdd}>
                  등록
                </button>
              </div>
            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHeader noButton={true}>
            제출 현황 ({loading ? "..." : list.length}건)
            <button
              className="btn btn-white btn-xs pull-right"
              onClick={() => setHometaxOpen(true)}
            >
              <i className="fa fa-download m-r-5"></i>소득공제신고 내역 데이터
            </button>
          </PanelHeader>
          <PanelBody className="p-0">
            <div className="table-responsive">
              <table className="table table-bordered table-striped m-b-0">
                <thead>
                  <tr>
                    <th>이름</th>
                    <th>연락처</th>
                    <th>주민등록번호</th>
                    <th>제출일시</th>
                    <th style={{ width: 80 }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {(loading || list.length === 0) && (
                    <tr>
                      <td colSpan={5} className="text-center">
                        {loading ? "불러오는 중..." : "제출된 내역이 없습니다."}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    list.map((it) => (
                      <tr key={it.id}>
                        <td>{it.name}</td>
                        <td>{it.phone}</td>
                        <td>{it.residentNumber}</td>
                        <td>
                          <ReactMoment format="YYYY-MM-DD HH:mm:ss">
                            {it.createdAt}
                          </ReactMoment>
                        </td>
                        <td align="center">
                          <button
                            className="btn btn-white btn-xs text-red"
                            onClick={() => onDelete(it.id)}
                          >
                            삭제
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </PanelBody>
        </Panel>

        <Modal isOpen={hometaxOpen} toggle={() => setHometaxOpen(false)}>
          <ModalHeader toggle={() => setHometaxOpen(false)}>
            소득공제신고 내역 데이터 다운로드
          </ModalHeader>
          <ModalBody>
            <div className="alert alert-info f-s-13 m-b-10">
              <i className="fa fa-info-circle m-r-5"></i>
              선택한 연도의 기부 건별 데이터를 홈택스 양식으로 내려받습니다.
              (DRM·DNT 모두 포함, 제출된 주민번호 반영)
            </div>
            <label className="f-w-600">귀속 연도</label>
            <select
              className="form-control"
              value={hometaxYear}
              onChange={(e) => setHometaxYear(parseInt(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}년
                </option>
              ))}
            </select>
          </ModalBody>
          <ModalFooter>
            <button
              className="btn btn-white"
              onClick={() => setHometaxOpen(false)}
            >
              취소
            </button>
            <button className="btn btn-primary" onClick={onDownloadHometax}>
              <i className="fa fa-download m-r-5"></i>다운로드
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
});
